import gql from 'graphql-tag';
import ReactFullStory, { FullStoryAPI } from 'react-fullstory';
import React, { Fragment, useEffect } from 'react';

import { isBrowser, isProduction } from '@zapier/common-utils';
import { ABTest } from '@zapier/toolbox-ab-testing';

import { Query } from '@apollo/react-components'; // eslint-disable-line no-restricted-syntax

export const FULLSTORY_ORG = '1XM';

const fullStoryQuery = gql`
  query FullStoryQuery {
    currentUserV2 {
      id
      fullName
      email
    }
  }
`;

const isInFrame = () => {
  try {
    return window.top !== window.self;
  } catch (e) {
    return true;
  }
};

const FullStoryScript = props => {
  const { user } = props;

  // If we're loading this page from inside an iframe it's on another partner's
  // site. Therefore, we include this global var to make sure that fullstory
  // records in the frame.
  // See https://help.fullstory.com/using/recording-iframes
  useEffect(() => {
    if (typeof window !== 'undefined' && isInFrame()) {
      window._fs_is_outer_script = true;
    }
  }, []);

  useEffect(() => {
    // Sets the identity of the user in Fullstory
    if (user && isProduction()) {
      FullStoryAPI('identify', user.id, {
        displayName: user.fullName,
        email: user.email,
      });
    }
  }, [user]);

  return <ReactFullStory org={FULLSTORY_ORG} />;
};

const FullStoryMount = () => (
  <Query query={fullStoryQuery} ssr={isBrowser()}>
    {result => {
      const { loading, error } = result;
      const { currentUserV2 } = result.data || {};

      if (loading || error) {
        return null;
      }

      return <FullStoryScript user={currentUserV2} />;
    }}
  </Query>
);

const FullStoryContainer = props => {
  if (!isProduction()) {
    return null;
  }

  // `forceRecordSession` is required to make FullStory to capture user
  // sessions for pages with relatively low traffic. New pages which are being
  // tested with a small number of users will not generate a large number of
  // FullStory sessions, because the AB test (below) which gates FullStory
  // rendering is rate limited to a subset of users. These low traffic pages
  // require a mechanism to force a 100% FullStory sampling rate.
  if (props.forceRecordSession) {
    return <FullStoryMount />;
  }

  return (
    <ABTest
      shouldTrackBirthEvent={false}
      testName="record-fullstory-session"
      variantRenderers={{
        control: () => <Fragment />,
        treatment: () => <FullStoryMount />,
      }}
    />
  );
};

export default FullStoryContainer;
